import { useId, useState, type ReactNode } from 'react'
import { InfoIcon } from '../icons'

// What the import field takes, said up front ("Works with …") and in full
// behind the (i): which links, what comes back from each, and how a link gets
// here on each phone (iOS gives web apps no share sheet, so iPhone pastes).
// The copy follows what the `recipe` function actually does — keep them in step.

const SOURCES: { name: string; what: string }[] = [
  { name: 'Recipe sites', what: 'Ingredients, method, time, the page’s rating and its photo.' },
  {
    name: 'From a photo or a book',
    what: 'On iPhone, open the photo or point the camera at the page, touch and hold the text, tap Select All, then Copy. Back here, tap Paste. Line breaks and hyphens from the page are put right.',
  },
  { name: 'YouTube', what: 'The recipe in the video’s description, or on the page it links to.' },
  { name: 'TikTok', what: 'The recipe in the caption, or on the page it links to. If TikTok cuts the caption short, paste it when asked.' },
  { name: 'Instagram', what: 'The recipe in the caption. If Instagram keeps the post private, paste the caption when asked.' },
]

/** The section's heading and hint, then `children` (the field), then the help when open. */
export function ImportHelp({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const panel = useId()
  return (
    <>
      <div className="ml-import__head">
        <h2 className="ml-label">Import a recipe</h2>
        <button type="button" className="ml-import__info" aria-expanded={open} aria-controls={panel}
          aria-label="Which links work?" onClick={() => setOpen(o => !o)}>
          <InfoIcon size={18} />
        </button>
      </div>
      <p className="ml-import__hint">Works with recipe sites, photos of a page, YouTube, TikTok and Instagram</p>
      {children}
      {open && (
        <div id={panel} className="ml-import-help" role="region" aria-label="What you can import">
          <dl className="ml-import-help__list">
            {SOURCES.map(s => (
              <div key={s.name} className="ml-import-help__row">
                <dt className="ml-import-help__name">{s.name}</dt>
                <dd className="ml-import-help__what">{s.what}</dd>
              </div>
            ))}
          </dl>
          <h3 className="ml-label ml-import-help__head">Bringing a link in</h3>
          <dl className="ml-import-help__list">
            <div className="ml-import-help__row">
              <dt className="ml-import-help__name">iPhone</dt>
              <dd className="ml-import-help__what">In the other app tap Share, then Copy link. Back here, tap Paste.</dd>
            </div>
            <div className="ml-import-help__row">
              <dt className="ml-import-help__name">Android</dt>
              <dd className="ml-import-help__what">Tap Share and pick Matlist.</dd>
            </div>
          </dl>
          <p className="ml-import-help__note">
            Anything without an ingredient list opens the editor, so you can fill it in. From a page, its text comes along to copy from.
          </p>
          <button type="button" className="ml-text-btn ml-import-help__close" onClick={() => setOpen(false)}>Got it</button>
        </div>
      )}
    </>
  )
}
